<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="520"
    scrollable
    content-class="audit-detail-dialog-wrapper">
    <v-card class="audit-detail-dialog" v-if="log">
      <!-- Compact Header -->
      <div class="detail-header">
        <div class="header-left">
          <div class="action-badge" :class="actionTypeClass">
            <v-icon size="16">{{ actionIcon }}</v-icon>
          </div>
          <div class="header-text">
            <span class="header-action">{{ actionLabel }}</span>
            <span class="header-time">{{ formattedDate }}</span>
          </div>
        </div>
        <v-btn icon x-small class="close-btn" @click="close">
          <v-icon size="18">mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="detail-loading">
        <v-progress-circular indeterminate color="primary" size="28" />
        <span>Loading changes...</span>
      </div>

      <!-- Content -->
      <div v-else class="detail-content">
        <!-- User Info Bar -->
        <div class="user-bar">
          <v-avatar size="28" :color="avatarColor" class="user-avatar">
            <span class="avatar-initials">{{ userInitials }}</span>
          </v-avatar>
          <div class="user-info">
            <span class="user-name">{{ log.userName || 'System' }}</span>
            <span class="user-action">{{ actionDescription }}</span>
          </div>
        </div>

        <!-- Field Changes - Card Style -->
        <div v-if="hasChanges" class="changes-list">
          <div class="changes-header">
            <span class="changes-title">Changes</span>
            <span class="changes-badge">{{ log.changes.length }}</span>
          </div>

          <div
            v-for="(change, idx) in log.changes"
            :key="idx"
            class="change-card">
            <div class="change-field">
              <span class="field-name">{{ change.displayName || change.fieldName }}</span>
              <span v-if="change.dataType && change.dataType !== 'text'" class="field-type">{{ change.dataType }}</span>
            </div>
            <div class="change-values">
              <div class="value-row value-old">
                <span class="value-indicator">−</span>
                <span class="value-content" :title="change.oldValue">
                  {{ formatDisplayValue(change.oldValue) }}
                </span>
              </div>
              <div class="value-row value-new">
                <span class="value-indicator">+</span>
                <span class="value-content" :title="change.newValue">
                  {{ formatDisplayValue(change.newValue) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- No Changes State -->
        <div v-else class="no-changes">
          <v-icon size="32" color="grey lighten-1">mdi-information-outline</v-icon>
          <span>No field-level changes recorded</span>
        </div>

        <!-- Metadata Section -->
        <div class="metadata-section" v-if="showMetadata">
          <div class="meta-row" v-if="log.affectedRowCount">
            <span class="meta-label">Rows affected</span>
            <span class="meta-value">{{ log.affectedRowCount }}</span>
          </div>
          <div class="meta-row" v-if="log.fileName">
            <span class="meta-label">File</span>
            <span class="meta-value meta-file">{{ log.fileName }}</span>
          </div>
          <div class="meta-row">
            <span class="meta-label">Log ID</span>
            <code class="meta-value meta-code">{{ log.id }}</code>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="detail-footer">
        <v-btn text small class="toggle-meta-btn" @click="showMetadata = !showMetadata">
          <v-icon size="14" left>{{ showMetadata ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          {{ showMetadata ? 'Hide' : 'Show' }} details
        </v-btn>
        <v-btn text small class="close-action" @click="close">
          Done
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'AuditLogDetailDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    log: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showMetadata: false
    };
  },
  computed: {
    actionIcon() {
      const icons = {
        Create: 'mdi-plus',
        Update: 'mdi-pencil',
        Delete: 'mdi-delete',
        Import: 'mdi-upload',
        Export: 'mdi-download',
        SchemaChange: 'mdi-table-cog',
        UserAssignment: 'mdi-account-plus',
        UserUnassignment: 'mdi-account-minus',
        ClearData: 'mdi-delete-sweep'
      };
      return icons[this.log?.actionType] || 'mdi-information';
    },
    actionTypeClass() {
      const classes = {
        Create: 'badge-create',
        Update: 'badge-update',
        Delete: 'badge-delete',
        Import: 'badge-import',
        Export: 'badge-export',
        SchemaChange: 'badge-schema',
        UserAssignment: 'badge-user',
        UserUnassignment: 'badge-user',
        ClearData: 'badge-delete'
      };
      return classes[this.log?.actionType] || 'badge-default';
    },
    actionLabel() {
      const labels = {
        Create: 'Created',
        Update: 'Updated',
        Delete: 'Deleted',
        Import: 'Imported',
        Export: 'Exported',
        SchemaChange: 'Schema Changed',
        UserAssignment: 'User Assigned',
        UserUnassignment: 'User Removed',
        ClearData: 'Data Cleared'
      };
      return labels[this.log?.actionType] || this.log?.actionType || 'Changed';
    },
    actionDescription() {
      const count = this.log?.changes?.length || this.log?.changeCount || 0;
      const actionType = this.log?.actionType;

      const descriptions = {
        Create: 'created this entry',
        Update: count === 1 ? 'updated 1 field' : `updated ${count} fields`,
        Delete: 'deleted this entry',
        Import: `imported ${this.log?.affectedRowCount || ''} rows`.trim(),
        Export: 'exported data',
        SchemaChange: 'modified the schema',
        UserAssignment: 'was assigned',
        UserUnassignment: 'was removed',
        ClearData: 'cleared all data'
      };
      return descriptions[actionType] || 'made changes';
    },
    formattedDate() {
      if (!this.log?.actionTimestamp) return '';
      const date = new Date(this.log.actionTimestamp);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    },
    hasChanges() {
      return this.log?.changes && this.log.changes.length > 0;
    },
    // User avatar
    userName() {
      if (this.log?.user?.displayName) return this.log.user.displayName;
      if (this.log?.user?.firstName) {
        return `${this.log.user.firstName} ${this.log.user.lastName || ''}`.trim();
      }
      return this.log?.userName || 'System';
    },
    userInitials() {
      const name = this.userName;
      if (!name || name === 'System') return 'SY';
      const parts = name.split(' ').filter(p => p.length > 0);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return name.slice(0, 2).toUpperCase();
    },
    avatarColor() {
      const colors = [
        '#F59E0B', '#F97316', '#EF4444', '#EC4899',
        '#8B5CF6', '#3B82F6', '#06B6D4', '#10B981'
      ];
      const name = this.userName;
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }
      return colors[Math.abs(hash) % colors.length];
    }
  },
  methods: {
    close() {
      this.$emit('input', false);
      this.$emit('close');
    },
    formatDisplayValue(value) {
      if (value === null || value === undefined || value === '') {
        return 'Empty';
      }
      const str = String(value);
      if (str.length > 80) {
        return str.slice(0, 80) + '…';
      }
      return str;
    }
  }
};
</script>

<style scoped>
/* ============================================
   AUDIT LOG DETAIL DIALOG
   Clean diff-style view for field changes
   ============================================ */

.audit-detail-dialog {
  border-radius: var(--ds-radius-lg) !important;
  overflow: hidden;
  background: var(--v-bg2-base);
}

/* Compact Header */
.detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: var(--v-bg3-base);
  border-bottom: 1px solid var(--ds-border-default);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.action-badge {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.badge-create { background: #10B981; }
.badge-update { background: var(--v-primary-base); }
.badge-delete { background: #EF4444; }
.badge-import { background: #8B5CF6; }
.badge-export { background: #6B7280; }
.badge-schema { background: #F59E0B; }
.badge-user { background: #3B82F6; }
.badge-default { background: #6B7280; }

.header-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.header-action {
  font-size: 14px;
  font-weight: 600;
  color: var(--v-text-base);
}

.header-time {
  font-size: 12px;
  color: var(--v-text_secondary-base);
}

.close-btn {
  color: var(--v-text_secondary-base) !important;
}

.close-btn:hover {
  color: var(--v-text-base) !important;
}

/* Loading State */
.detail-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  gap: 12px;
  color: var(--v-text_secondary-base);
  font-size: 13px;
}

/* Content Area */
.detail-content {
  padding: 16px;
  max-height: 420px;
  overflow-y: auto;
}

/* User Info Bar */
.user-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 14px;
  margin-bottom: 14px;
  border-bottom: 1px solid var(--ds-border-subtle);
}

.user-avatar {
  flex-shrink: 0;
}

.avatar-initials {
  font-size: 10px;
  font-weight: 700;
  color: white;
  letter-spacing: 0.02em;
}

.user-info {
  display: flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
}

.user-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--v-text-base);
}

.user-action {
  font-size: 13px;
  color: var(--v-text_secondary-base);
}

/* Changes List */
.changes-list {
  margin-bottom: 16px;
}

.changes-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.changes-title {
  font-size: 11px;
  font-weight: 600;
  color: var(--v-text_secondary-base);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.changes-badge {
  font-size: 10px;
  font-weight: 600;
  color: var(--v-primary-base);
  background: rgba(var(--v-primary-base-rgb), 0.1);
  padding: 2px 6px;
  border-radius: 10px;
}

/* Change Card - Diff Style */
.change-card {
  background: var(--v-bg3-base);
  border: 1px solid var(--ds-border-default);
  border-radius: 8px;
  margin-bottom: 8px;
  overflow: hidden;
}

.change-field {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: var(--v-bg2-base);
  border-bottom: 1px solid var(--ds-border-subtle);
}

.field-name {
  font-size: 12px;
  font-weight: 600;
  color: var(--v-text-base);
}

.field-type {
  font-size: 10px;
  color: var(--v-text_secondary-base);
  padding: 1px 5px;
  background: var(--v-bg3-base);
  border-radius: 3px;
  font-family: 'Roboto Mono', monospace;
}

/* Value Rows - Diff Style */
.change-values {
  padding: 0;
}

.value-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 6px 12px;
  font-size: 13px;
  line-height: 1.4;
}

.value-row + .value-row {
  border-top: 1px solid var(--ds-border-subtle);
}

.value-old {
  background: rgba(239, 68, 68, 0.06);
}

.value-new {
  background: rgba(16, 185, 129, 0.06);
}

.value-indicator {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  font-size: 12px;
  font-weight: 700;
  font-family: 'Roboto Mono', monospace;
  margin-top: 1px;
}

.value-old .value-indicator {
  background: #FEE2E2;
  color: #DC2626;
}

.value-new .value-indicator {
  background: #D1FAE5;
  color: #059669;
}

.value-content {
  flex: 1;
  word-break: break-word;
  color: var(--v-text-base);
}

.value-old .value-content {
  color: #991B1B;
}

.value-new .value-content {
  color: #065F46;
  font-weight: 500;
}

/* No Changes State */
.no-changes {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  gap: 8px;
  color: var(--v-text_secondary-base);
  font-size: 13px;
}

/* Metadata Section */
.metadata-section {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid var(--ds-border-subtle);
}

.meta-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  font-size: 12px;
}

.meta-label {
  color: var(--v-text_secondary-base);
}

.meta-value {
  color: var(--v-text-base);
  font-weight: 500;
}

.meta-file {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.meta-code {
  font-family: 'Roboto Mono', monospace;
  font-size: 11px;
  background: var(--v-bg3-base);
  padding: 2px 6px;
  border-radius: 4px;
}

/* Footer */
.detail-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  border-top: 1px solid var(--ds-border-default);
  background: var(--v-bg3-base);
}

.toggle-meta-btn {
  text-transform: none;
  font-weight: 500;
  font-size: 12px;
  color: var(--v-text_secondary-base) !important;
}

.close-action {
  text-transform: none;
  font-weight: 600;
  color: var(--v-primary-base) !important;
}

/* Dark mode adjustments for diff colors */
.theme--dark .value-old {
  background: rgba(239, 68, 68, 0.12);
}

.theme--dark .value-new {
  background: rgba(16, 185, 129, 0.12);
}

.theme--dark .value-old .value-indicator {
  background: rgba(220, 38, 38, 0.3);
  color: #FCA5A5;
}

.theme--dark .value-new .value-indicator {
  background: rgba(5, 150, 105, 0.3);
  color: #6EE7B7;
}

.theme--dark .value-old .value-content {
  color: #FCA5A5;
}

.theme--dark .value-new .value-content {
  color: #6EE7B7;
}

/* Scrollbar styling */
.detail-content::-webkit-scrollbar {
  width: 6px;
}

.detail-content::-webkit-scrollbar-track {
  background: transparent;
}

.detail-content::-webkit-scrollbar-thumb {
  background: var(--ds-border-default);
  border-radius: 3px;
}

.detail-content::-webkit-scrollbar-thumb:hover {
  background: var(--v-text_secondary-base);
}
</style>
